import { useChatbotsLimits, useChatbotsTrial } from "@zapier/client";
import { Button, Link, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { logWithNotification } from "@zapier/shared";
import {
  CHATBOT_CREATE_ROUTE,
  MONOLITH_BASE_URL,
  getChatbotsIndexUrl,
  useNotification,
} from "@zapier/utils";
import { Icon } from "@zapier/zinnia-icons";
import { ErrorBoundary } from "react-error-boundary";
import styles from "./ChatbotsTrialBanner.module.css";

const getUpgradeUrl = () => {
  const redirectUrl = encodeURIComponent(
    `${MONOLITH_BASE_URL}${getChatbotsIndexUrl()}`,
  );
  return `${MONOLITH_BASE_URL}/app/planbuilder/add-ons?chatbots=chatbots-advanced&redirectUrl=${redirectUrl}`;
};

function ChatbotsTrialBannerInner() {
  const t = useTranslations("chatbots-trial-banner");
  const { createNotification } = useNotification();
  const { planName, isLoading: isLoadingLimits } = useChatbotsLimits();
  const {
    trial,
    extendTrial,
    isExtendingTrial,
    isLoading: isLoadingTrial,
  } = useChatbotsTrial();
  const upgradeUrl = getUpgradeUrl();

  const handleExtendTrial = () => {
    if (!trial) return;
    extendTrial(
      { trialId: trial.id },
      {
        onError: () => {
          createNotification({
            message: t("notifications.extend-error"),
            type: "error",
          });
        },
      },
    );
  };

  // Wait for data to load before rendering
  if (isLoadingLimits || isLoadingTrial) {
    return;
  }

  // Only show for free or trial plans
  if (planName !== "trial" && planName !== "free") {
    return;
  }

  // No trial - show start trial banner
  if (!trial) {
    return (
      <aside className={styles.wrapper}>
        <Text tag="h2" type="ParagraphHeader4" color="TextDefault">
          {t("headings.start-trial")}
        </Text>
        <p className={styles.bannerCopy}>
          {t.rich("descriptions.trial-benefits", {
            advancedPlanLink: (chunks) => (
              <a className={styles.planLink} href={upgradeUrl}>
                {chunks}
              </a>
            ),
          })}
        </p>
        <div className={styles.buttons}>
          <Link variant="brand" href={CHATBOT_CREATE_ROUTE}>
            {t("buttons.start-trial")}
          </Link>
        </div>
      </aside>
    );
  }

  // Trial expired and already extended - only show upgrade
  if (trial.isExpired && trial.extendedAt !== null) {
    return (
      <aside className={styles.wrapper}>
        <div className={styles.bannerHeader}>
          <Icon color="BrandOrange" name="navAIChatbot" size={20} />
          <Text tag="h2" type="ParagraphHeader4" color="TextDefault">
            {t("headings.trial-ended")}
          </Text>
        </div>
        <p className={styles.bannerCopy}>
          {t("descriptions.upgrade-required")}
        </p>
        <div className={styles.buttons}>
          <Link variant="brand" href={upgradeUrl}>
            {t("buttons.upgrade")}
          </Link>
        </div>
      </aside>
    );
  }

  // Trial expired but can be extended
  if (trial.isExpired && trial.extendedAt === null) {
    return (
      <aside className={styles.wrapper}>
        <div className={styles.bannerHeader}>
          <Icon color="BrandOrange" name="navAIChatbot" size={20} />
          <Text tag="h2" type="ParagraphHeader4" color="TextDefault">
            {t("headings.trial-ended-extendable")}
          </Text>
        </div>
        <p className={styles.bannerCopy}>
          {t("descriptions.upgrade-or-extend")}
        </p>
        <div className={styles.buttons}>
          <Link variant="brand" href={upgradeUrl}>
            {t("buttons.upgrade")}
          </Link>
          <Button
            variant="tertiary"
            isDisabled={isExtendingTrial}
            isPending={isExtendingTrial}
            onPress={handleExtendTrial}
          >
            {t("buttons.extend-trial")}
          </Button>
        </div>
      </aside>
    );
  }

  // Trial is active
  return (
    <aside className={styles.wrapper}>
      <div className={styles.bannerHeader}>
        <Icon color="BrandOrange" name="navAIChatbot" size={20} />
        <Text tag="h2" type="ParagraphHeader4" color="TextDefault">
          {t("headings.trial-active")}
        </Text>
        <Text tag="span" type="SmallPrint2" color="TextWeaker">
          {t("labels.days-remaining", {
            trialRemaining: trial.remaining ?? "",
          })}
        </Text>
      </div>
      <p className={styles.bannerCopy}>
        {t.rich("descriptions.active-trial-benefits", {
          advancedPlanLink: (chunks) => (
            <a className={styles.planLink} href={upgradeUrl}>
              {chunks}
            </a>
          ),
        })}
      </p>
      <div className={styles.buttons}>
        <Link variant="brand" href={upgradeUrl}>
          {t("buttons.upgrade")}
        </Link>
      </div>
    </aside>
  );
}

export function ChatbotsTrialBanner() {
  return (
    <ErrorBoundary
      fallbackRender={() => <></>}
      onError={(error, info) => {
        const message = error instanceof Error ? error.message : String(error);
        logWithNotification(
          `Error in ChatbotsTrialBanner: ${message}. Info: ${info.componentStack}`,
          "error",
        );
      }}
    >
      <ChatbotsTrialBannerInner />
    </ErrorBoundary>
  );
}
